import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listHostInvoices, openInvoicePdf } from '../../api/invoices';
import { useToast } from '../../components/ToastContext';

const typeLabels = {
  HOST_SETTLEMENT: 'Host settlement',
  HV_TO_HOST_COMMISSION: 'Commission invoice',
};

const badgeStyles = {
  issued: 'bg-emerald-100 text-emerald-700',
  void: 'bg-amber-100 text-amber-700',
  refunded: 'bg-rose-100 text-rose-700',
  default: 'bg-slate-100 text-slate-700',
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = badgeStyles[key] || badgeStyles.default;
  const label = String(value || 'unknown').replace(/_/g, ' ');
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${cls}`}>{label}</span>;
};

const formatDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatAmount = (currency, amount) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return `${currency || ''} ${amount || ''}`.trim();
  return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const typeOptions = [
  { value: '', label: 'All types' },
  { value: 'HOST_SETTLEMENT', label: 'Host settlement' },
  { value: 'HV_TO_HOST_COMMISSION', label: 'Commission invoice' },
];

const InvoicesList = ({ accessToken }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [type, setType] = useState('');

  const load = async (nextPage = page, nextType = type) => {
    setLoading(true);
    setError('');
    try {
      const res = await listHostInvoices(
        { page: nextPage, limit: pagination.limit, type: nextType },
        accessToken
      );
      setItems(res.data || []);
      setPagination(res.pagination || { page: nextPage, totalPages: 1, total: 0, limit: pagination.limit });
    } catch (err) {
      setError(err.message || 'Unable to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    load(page, type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type, accessToken]);

  const openPdf = async (invoiceId) => {
    try {
      await openInvoicePdf(invoiceId, accessToken, { download: true });
    } catch (err) {
      if (err.code === 'POPUP_BLOCKED') {
        toast.error('Popup blocked. Please allow popups to download invoices.');
        return;
      }
      toast.error(err.message || 'Unable to open invoice');
    }
  };

  const printPdf = async (invoiceId) => {
    try {
      await openInvoicePdf(invoiceId, accessToken, { print: true });
    } catch (err) {
      if (err.code === 'POPUP_BLOCKED') {
        toast.error('Popup blocked. Please allow popups to print invoices.');
        return;
      }
      toast.error(err.message || 'Unable to print invoice');
    }
  };

  const pageSummary = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total || 0);
    if (!pagination.total) return '0 results';
    return `${start}-${end} of ${pagination.total}`;
  }, [pagination]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Invoices</p>
          <h2 className="text-2xl font-semibold text-slate-900">Settlement statements and commission invoices.</h2>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{pageSummary}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
        >
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {error && <div className="px-4 py-3 text-sm text-rose-600">{error}</div>}
        {loading && <div className="px-4 py-3 text-sm text-slate-500">Loading invoices...</div>}

        {!loading && !error && items.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500">No invoices available yet.</div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3">Invoice</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Booking</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((invoice) => {
                  const total = invoice.totals?.grandTotal ?? invoice.totals?.total ?? 0;
                  return (
                    <tr key={invoice.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-slate-900">{invoice.invoiceNumber}</td>
                      <td className="px-4 py-3 text-slate-700">
                        {typeLabels[invoice.invoiceType] || invoice.invoiceType}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{invoice.stayDetails?.bookingCode || invoice.bookingId}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(invoice.issuedAt)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {formatAmount(invoice.currency, total)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge value={invoice.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex flex-wrap gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => navigate(`/host/invoices/${invoice.id}`)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-200 hover:text-emerald-700"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => openPdf(invoice.id)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-200 hover:text-emerald-700"
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            onClick={() => printPdf(invoice.id)}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            Print
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Previous
          </button>
          <div className="text-slate-600">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
            disabled={page >= pagination.totalPages}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesList;
